import { useState, useEffect, useCallback, useRef } from 'react';
import { websocketService } from '../services/websocketService';
import type { 
  WebSocketConnection, 
  WebSocketStats, 
  ProfileConnectionInfo 
} from '../types/api';

interface UseWebSocketMonitorReturn {
  // Data
  stats: WebSocketStats | null;
  connections: WebSocketConnection[];
  profileConnections: ProfileConnectionInfo[];

  // State
  isLoading: boolean;
  error: string;
  lastUpdate: Date | null;

  // Actions
  loadData: () => Promise<void>;
  disconnectConnection: (connectionId: string) => Promise<void>;
  broadcastMessage: (message: any) => Promise<void>;
  clearError: () => void;

  // Settings
  autoRefresh: boolean;
  setAutoRefresh: (enabled: boolean) => void;
}

export const useWebSocketMonitor = (): UseWebSocketMonitorReturn => {
  const [stats, setStats] = useState<WebSocketStats | null>(null);
  const [connections, setConnections] = useState<WebSocketConnection[]>([]);
  const [profileConnections, setProfileConnections] = useState<ProfileConnectionInfo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const loadData = useCallback(async () => {
    try {
      setError('');
      
      const [statsResponse, connectionsResponse, profilesResponse] = await Promise.all([
        websocketService.getConnectionStats(),
        websocketService.getActiveConnections(),
        websocketService.getProfileConnections()
      ]);

      if (statsResponse.success && statsResponse.data) {
        setStats(statsResponse.data);
      } else {
        throw new Error(statsResponse.message || 'Failed to load stats');
      }

      if (connectionsResponse.success && connectionsResponse.data) {
        setConnections(connectionsResponse.data);
      } else {
        throw new Error(connectionsResponse.message || 'Failed to load connections');
      }

      if (profilesResponse.success && profilesResponse.data) {
        setProfileConnections(profilesResponse.data);
      } else {
        throw new Error(profilesResponse.message || 'Failed to load profile connections');
      }

      setLastUpdate(new Date());
    } catch (error: any) {
      setError(error.message || 'Failed to load WebSocket data');
    } finally {
      setIsLoading(false);
    }
  }, []);



  const disconnectConnection = useCallback(async (connectionId: string) => {
    try {
      const response = await websocketService.disconnectConnection(connectionId);
      if (response.success) {
        // Remove the connection from local state immediately for better UX
        setConnections(prev => prev.filter(conn => conn.id !== connectionId));
        // Reload data to get updated stats
        loadData();
      } else {
        throw new Error(response.message || 'Failed to disconnect connection');
      }
    } catch (error: any) {
      setError(error.message || 'Failed to disconnect connection');
    }
  }, [loadData]);

  const broadcastMessage = useCallback(async (message: any) => {
    try {
      const response = await websocketService.broadcastMessage(message);
      if (!response.success) {
        throw new Error(response.message || 'Failed to broadcast message');
      }
    } catch (error: any) {
      setError(error.message || 'Failed to broadcast message');
      throw error;
    }
  }, []);

  const clearError = useCallback(() => {
    setError('');
  }, []);

  // Auto-refresh effect
  useEffect(() => {
    if (!autoRefresh) return;

    refreshIntervalRef.current = setInterval(() => {
      loadData();
    }, 5000); // Refresh every 5 seconds

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [autoRefresh, loadData]);

  // Initial load
  useEffect(() => {
    loadData();
  }, [loadData]);

  return {
    // Data
    stats,
    connections,
    profileConnections,

    // State
    isLoading,
    error,
    lastUpdate,

    // Actions
    loadData,
    disconnectConnection,
    broadcastMessage,
    clearError,

    // Settings
    autoRefresh,
    setAutoRefresh,
  };
};
